import type { PixelStreaming } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import {
  addConnectionManagerListener,
  PixelStreamConnectionManager,
  removeConnectionManagerListener,
} from 'features/kestrelViewer/connection/pixelStreamConnectionManager'
import type {
  ConnectionConfig,
  ConnectionState,
  ResetRequestEventDetail,
} from 'features/kestrelViewer/connection/types'
import { CONNECTION_STATUS_TO_LOADING_STEP } from 'features/kestrelViewer/connection/types'
import type { ParsedMessage } from 'features/kestrelViewer/utils/messageUtils'
import { useCallback, useEffect, useRef, useState } from 'react'

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UsePixelStreamConnectionResult {
  /** The current connection state */
  connectionState: ConnectionState

  /** The PixelStreaming instance (undefined if not connected) */
  pixelStreaming: PixelStreaming | undefined

  /** Whether the connection is ready for streaming */
  isReady: boolean

  /** Send a command to Unreal Engine */
  sendCommand: (json: string | object) => boolean

  /** Manually trigger a disconnect */
  disconnect: () => void

  /**
   * The loading step corresponding to the current connection state.
   * Use this to update the loading UI.
   */
  loadingStep: (typeof CONNECTION_STATUS_TO_LOADING_STEP)[keyof typeof CONNECTION_STATUS_TO_LOADING_STEP]
}

// ============================================================================
// Hook Options
// ============================================================================

export interface UsePixelStreamConnectionOptions {
  /** The signalling server URL (connection starts when this is set) */
  url: string | undefined

  /** Reference to the video parent element */
  videoParentRef: React.RefObject<HTMLDivElement | null>

  /** Authorization token (Bearer token) */
  authToken: string | undefined

  /** Whether we're in dev mode (skips auth) */
  devMode: boolean

  /** Timeout for waiting for streamer (default: 10000ms) */
  streamerTimeoutMs?: number

  /** The Kestrel model ID (for tracing) */
  kestrelModelId?: string

  /** The UUID of the provisioned VM (for tracing) */
  vmId?: string

  /** Callback when a message is received from Unreal */
  onMessage?: (message: ParsedMessage) => void

  /** Callback when a reset is requested (timeout, subscribe failed, auth failed) */
  onResetRequest?: (reason: ResetRequestEventDetail['reason']) => void

  /** Callback when an error occurs */
  onError?: (error: string) => void
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React hook that wraps PixelStreamConnectionManager.
 *
 * This hook:
 * - Creates and manages a single ConnectionManager instance
 * - Automatically connects when url and authToken are available
 * - Exposes connection state as React state
 * - Forwards events to callbacks
 * - Handles cleanup on unmount
 *
 * @example
 * ```tsx
 * const {
 *   connectionState,
 *   pixelStreaming,
 *   isReady,
 *   sendCommand,
 * } = usePixelStreamConnection({
 *   url: pixelStreamingURL,
 *   videoParentRef,
 *   authToken: authorization,
 *   devMode,
 *   onMessage: handleMessage,
 *   onResetRequest: handleResetRequest,
 * })
 * ```
 */
export function usePixelStreamConnection(
  options: UsePixelStreamConnectionOptions
): UsePixelStreamConnectionResult {
  const {
    url,
    videoParentRef,
    authToken,
    devMode,
    streamerTimeoutMs,
    kestrelModelId,
    vmId,
    onMessage,
    onResetRequest,
    onError,
  } = options

  // Single manager instance for the lifetime of the hook
  const managerRef = useRef<PixelStreamConnectionManager | null>(null)

  // Get or create the manager
  if (!managerRef.current) {
    managerRef.current = new PixelStreamConnectionManager()
  }

  // Connection state (mirrors manager state, triggers re-renders)
  const [connectionState, setConnectionState] = useState<ConnectionState>(() =>
    managerRef.current!.getState()
  )

  // Derived values
  const isReady = connectionState.status === 'ready'
  const pixelStreaming =
    'instance' in connectionState ? connectionState.instance : undefined
  const loadingStep = CONNECTION_STATUS_TO_LOADING_STEP[connectionState.status]

  // ============================================================================
  // Event Subscriptions
  // ============================================================================

  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return

    // State change handler - updates React state
    const handleStateChange = (event: CustomEvent) => {
      const { state } = event.detail as { state: ConnectionState }
      setConnectionState(state)
    }

    // Message handler - forwards to callback
    const handleMessage = (event: CustomEvent) => {
      const { message } = event.detail as { message: ParsedMessage }
      onMessage?.(message)
    }

    // Reset request handler - forwards to callback
    const handleResetRequest = (event: CustomEvent) => {
      const { reason } = event.detail as ResetRequestEventDetail
      onResetRequest?.(reason)
    }

    // Error handler - forwards to callback
    const handleError = (event: CustomEvent) => {
      const { error } = event.detail as {
        error: string
        recoverable: boolean
      }
      onError?.(error)
    }

    addConnectionManagerListener(manager, 'stateChange', handleStateChange)
    addConnectionManagerListener(manager, 'message', handleMessage)
    addConnectionManagerListener(manager, 'resetRequest', handleResetRequest)
    addConnectionManagerListener(manager, 'error', handleError)

    return () => {
      removeConnectionManagerListener(manager, 'stateChange', handleStateChange)
      removeConnectionManagerListener(manager, 'message', handleMessage)
      removeConnectionManagerListener(
        manager,
        'resetRequest',
        handleResetRequest
      )
      removeConnectionManagerListener(manager, 'error', handleError)
    }
  }, [onMessage, onResetRequest, onError])

  // ============================================================================
  // Connection Lifecycle
  // ============================================================================

  useEffect(() => {
    const manager = managerRef.current
    if (!manager) return

    // Check if we have all required config
    const videoParent = videoParentRef.current
    if (!url || !videoParent || !authToken) {
      // Missing config - disconnect if currently connected
      manager.disconnect()
      return
    }

    // Build config
    const config: ConnectionConfig = {
      url,
      videoParent,
      authToken,
      devMode,
      streamerTimeoutMs,
      kestrelModelId,
      vmId,
    }

    // Start connection (async, but we don't need to await)
    // The manager handles "already connecting" by aborting the previous attempt
    manager.connect(config)

    // Cleanup on unmount or when dependencies change
    return () => {
      manager.disconnect()
    }
  }, [
    url,
    authToken,
    devMode,
    streamerTimeoutMs,
    kestrelModelId,
    vmId,
    videoParentRef,
  ])

  // ============================================================================
  // Public Methods
  // ============================================================================

  const sendCommand = useCallback((json: string | object): boolean => {
    const manager = managerRef.current
    if (!manager) return false
    return manager.sendCommand(json)
  }, [])

  const disconnect = useCallback(() => {
    const manager = managerRef.current
    if (!manager) return
    manager.disconnect()
  }, [])

  return {
    connectionState,
    pixelStreaming,
    isReady,
    sendCommand,
    disconnect,
    loadingStep,
  }
}
